import { Button } from "@wellcheck/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@wellcheck/components/ui/dialog";

import type { PeriodInfoInput } from "@/features/upload/data/periodInfoSchema";
import type { Respondent } from "@/features/upload/data/respondentsCsv";

import { langLabel } from "@/features/upload/data/respondentsCsv";

export interface Step2ConfirmProps {
  periodInfo: PeriodInfoInput;
  respondents: Respondent[];
  /**
   * 確認ダイアログの open 状態。トリガーは Wizard フッターの「確定して送信」が持つ。
   * ボタン位置を他ステップと揃えるため Step2Confirm 側にトリガーを置かない。
   */
  dialogOpen: boolean;
  onDialogOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  submitError?: string;
}

/**
 * Step2Confirm — 送信前の最終確認画面。
 *
 * 入力内容 (実施期間 / 特記事項 / 名簿) を読み取りで再提示し、
 * Wizard フッターの「確定して送信」が開く確認ダイアログを描画する。
 * 実際の送信処理は親から渡される onConfirm に委譲する (mock では console.log)。
 */
export function Step2Confirm({
  periodInfo,
  respondents,
  dialogOpen,
  onDialogOpenChange,
  onConfirm,
  submitError,
}: Step2ConfirmProps) {
  const handleConfirm = () => {
    onDialogOpenChange(false);
    onConfirm();
  };

  return (
    <div className="space-y-6">
      <SummaryCard title="実施期間">
        <SummaryRow label="開始日" value={periodInfo.periodStart} />
        <SummaryRow label="終了日" value={periodInfo.periodEnd} />
      </SummaryCard>

      <SummaryCard title="名簿">
        <SummaryRow label="登録件数" value={`${respondents.length} 件`} />
        {respondents.length > 0 && (
          <div className="mt-3 max-h-[60vh] overflow-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">氏名</th>
                  <th className="px-3 py-2 text-left font-medium">社員番号</th>
                  <th className="px-3 py-2 text-left font-medium">性別</th>
                  <th className="px-3 py-2 text-left font-medium">メールアドレス</th>
                  <th className="px-3 py-2 text-left font-medium">部署名</th>
                  <th className="px-3 py-2 text-left font-medium">言語</th>
                </tr>
              </thead>
              <tbody>
                {respondents.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2">{r.fullName}</td>
                    <td className="px-3 py-2">{r.employeeNumber}</td>
                    <td className="px-3 py-2">{r.gender}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.email}</td>
                    <td className="px-3 py-2">{r.department}</td>
                    <td className="px-3 py-2">{langLabel(r.lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SummaryCard>

      {periodInfo.notes && (
        <SummaryCard title="特記事項">
          <p className="whitespace-pre-wrap text-sm">{periodInfo.notes}</p>
        </SummaryCard>
      )}

      {submitError && (
        <p role="alert" className="text-sm text-destructive">
          {submitError}
        </p>
      )}

      <Dialog open={dialogOpen} onOpenChange={onDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>送信してよろしいですか？</DialogTitle>
            <DialogDescription>
              送信後は編集できません。修正が必要な場合は弊社担当者へご連絡ください。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => onDialogOpenChange(false)}>
              キャンセル
            </Button>
            <Button onClick={handleConfirm}>送信する</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-md border border-border bg-card px-4 py-4">
      <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
